import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const LouiseTrinitIA = () => {
  const { t } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(null);
  const [tiktokComments, setTiktokComments] = useState([]);

  // Fonction helper pour encoder correctement les URLs avec espaces et caractères spéciaux
  const encodeImageUrl = (filepath) => {
    // Sépare le chemin du nom de fichier
    const lastSlash = filepath.lastIndexOf('/');
    const path = filepath.substring(0, lastSlash + 1);
    const filename = filepath.substring(lastSlash + 1);
    // Encode seulement le nom de fichier, pas le chemin
    return path + encodeURIComponent(filename);
  };

  // Photos de Louise - fichiers dans public/louise/
  const photos = [
    { id: 1, url: '/louise/9cd63ffa-022c-4ee8-a954-c536c995b228.png', caption: 'Louise - Photo 1' },
    { id: 2, url: encodeImageUrl('/louise/Gemini_Generated_Image_68ykzt68ykzt68yk copie.png'), caption: 'Louise - Photo 2' },
    { id: 3, url: '/louise/Gemini_Generated_Image_z0k9e0z0k9e0z0k9.png', caption: 'Louise - Photo 3' },
    { id: 4, url: encodeImageUrl('/louise/Generated Image January 15, 2026 - 5_53PM.jpeg'), caption: 'Louise - Photo 4' },
    { id: 5, url: '/louise/hedra-image-0c5f2718-e87f-40dc-a772-91b4d94a5ab4.png', caption: 'Louise - Photo 5' },
    { id: 6, url: '/louise/hedra-image-f9ac9d5e-9be5-46f3-879c-c0ae2a1f1f77.png', caption: 'Louise - Photo 6' },
  ];

  // Charger les commentaires TikTok depuis le fichier JSON
  useEffect(() => {
    fetch('/louise/tiktok-comments.json')
      .then(response => response.json())
      .then(data => setTiktokComments(data))
      .catch(error => {
        console.error('Erreur lors du chargement des commentaires TikTok:', error);
        // Fallback avec des commentaires par défaut en cas d'erreur
        setTiktokComments([
          {
            id: 1,
            username: '@user1',
            comment: 'Incroyable ! Elle est tellement réaliste ! 🔥',
            likes: 1250,
            date: 'Il y a 2 jours'
          }
        ]);
      });
  }, []);

  return (
    <section className="pt-32 pb-24 px-6 bg-gradient-to-b from-black to-[#050505] relative min-h-screen">
      <div className="pexadia-watermark"></div>
      <div className="max-w-7xl mx-auto relative z-20">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-1.5 mb-6 border border-[#d4af37]/30 rounded-full bg-[#d4af37]/5">
            <span className="text-[10px] font-black tracking-[0.3em] gold-gradient uppercase">
              {t.louise.badge}
            </span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-serif mb-6 italic leading-tight">
            <span className="gold-gradient">{t.louise.title}</span>
          </h1>
          
          <p className="text-gray-400 max-w-2xl mx-auto text-xl font-light leading-relaxed">
            {t.louise.description}
          </p>
        </div>

        {/* Galerie de Photos */}
        <div className="mb-24">
          <h2 className="text-3xl font-serif mb-8 text-center gold-gradient">
            {t.louise.gallery.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {photos.map((photo) => (
              <div
                key={photo.id}
                className="group relative overflow-hidden rounded-lg border border-white/10 hover:border-[#d4af37]/50 transition-all cursor-pointer"
                onClick={() => setSelectedImage(photo)}
              >
                <div className="aspect-square bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] flex items-center justify-center relative">
                  <img
                    src={photo.url}
                    alt={photo.caption}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const placeholder = e.target.parentElement.querySelector('.image-placeholder');
                      if (placeholder) placeholder.style.display = 'flex';
                    }}
                  />
                  <div className="hidden image-placeholder w-full h-full items-center justify-center text-gray-500 absolute inset-0">
                    <div className="text-center">
                      <i className="fas fa-image text-4xl mb-2"></i>
                      <p className="text-xs text-gray-600">Image non disponible</p>
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-white font-semibold">{photo.caption}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Commentaires TikTok */}
        <div>
          <h2 className="text-3xl font-serif mb-8 text-center gold-gradient">
            {t.louise.comments.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {tiktokComments.map((comment) => (
              <div
                key={comment.id}
                className="glass-card p-6 rounded-lg border border-white/10 hover:border-[#d4af37]/30 transition-all"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#d4af37] to-[#b8941f] flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-user text-black text-xl"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-white">{comment.username}</h3>
                      <span className="text-xs text-gray-500">{comment.date}</span>
                    </div>
                    <p className="text-gray-300 leading-relaxed mb-3">{comment.comment}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-heart text-[#d4af37]"></i>
                        <span>{comment.likes.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="fab fa-tiktok text-white"></i>
                        <span>TikTok</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal pour image agrandie */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              className="absolute top-4 right-4 text-white text-3xl hover:text-[#d4af37] transition-colors z-10"
              onClick={() => setSelectedImage(null)}
            >
              <i className="fas fa-times"></i>
            </button>
            <img
              src={selectedImage.url}
              alt={selectedImage.caption}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onError={(e) => {
                e.target.style.display = 'none';
                const errorDiv = document.createElement('div');
                errorDiv.className = 'text-white text-center p-8';
                errorDiv.textContent = 'Image non disponible';
                e.target.parentElement.appendChild(errorDiv);
              }}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default LouiseTrinitIA;
